import axiosInstance from './axiosConfig';

export interface CicloHorasDomiciliado {
    id: number;
    numero_mes: number;
    fecha_inicio: string;
    fecha_fin: string;
    horas_asignadas: number | string;
    horas_consumidas: number | string;
    horas_disponibles: number | string;
    es_activo: boolean;
    fecha_creacion: string;
}

export interface Afiliacion {
    cita?: number | null;
    formulario_habilitado?: boolean;
    id: number;
    cliente: number;
    cliente_nombre?: string;
    cliente_email?: string;
    cliente_telefono?: string;
    cliente_rol?: string;
    empresa_nombre: string;
    nit?: string;
    representante_legal?: string;
    telefono_contacto?: string;
    correo_contacto?: string;
    rubro?: string;
    precio_plan_servicio?: number;

    tipo_contrato?: 'mensual' | 'anual';
    tipo_corte_ciclo?: 'fecha_pago' | 'mes_calendario';

    // Fase 1
    paso1_contrato_enviado: boolean;
    paso1_fecha?: string;
    paso1_archivo?: string;

    paso2_contrato_firmado: boolean;
    paso2_fecha?: string;
    paso2_archivo?: string;

    // Fase 2
    paso3_docs_fiscales_enviados: boolean;
    paso3_fecha?: string;
    paso3_factura_servicio?: string;
    paso3_croquis?: string;

    paso4_requisitos_recibidos: boolean;
    paso4_fecha?: string;
    paso4_roe?: string;
    paso4_matricula?: string;
    paso4_ultima_factura?: string;

    paso5_nit_exhibido: boolean;
    paso5_fecha?: string;
    paso5_archivo_nit?: string;
    paso5_foto_exhibicion?: string;

    // Fase 3
    paso6_kardex_recepcion_abierto: boolean;
    paso6_fecha?: string;
    paso6_observaciones?: string;

    paso7_kardex_horas_abierto: boolean;
    paso7_fecha?: string;

    // Horas gratuitas
    horas_gratuitas_asignadas: number;
    horas_gratuitas_consumidas: number | string;
    horas_gratuitas_disponibles: number | string;
    minutos_disponibles: number;

    ciclos_horas?: CicloHorasDomiciliado[];
    ciclo_actual_info?: CicloHorasDomiciliado | null;

    pasos_completados_count: number;
    porcentaje_progreso: number;

    uso_horas_regalo_actual?: {
        id: number;
        config_horas_regalo: number;
        config_horas_display: string;
        horas_regalo_aplicadas: number;
        horas_usadas: number;
        minutos_disponibles: number;
        fecha_inicio_periodo: string;
        fecha_fin_periodo: string;
    } | null;
    correspondencias_count?: number;

    // Pagos y Planes de Domiciliación
    monto_total?: number | string;
    monto_pagado?: number | string;
    saldo_pendiente?: number | string;
    estado_pago?: 'pagado' | 'parcial' | 'pendiente' | 'no_aplica';
    pagos?: any[];

    config_horas_regalo_info?: {
        id: number;
        horas_asignadas: number;
        activo: boolean;
        servicio_nombre?: string;
    } | null;

    config_correspondencia_info?: {
        id: number;
        descripcion?: string;
        estado?: boolean;
        servicio_nombre?: string;
    } | null;

    servicio_nombre?: string;
    precio_plan_servicio_info?: {
        id: number;
        precio_inicial: string;
        servicio_id: number;
        servicio_nombre: string;
    } | null;

    periodo_inicio?: string;
    periodo_fin?: string;
    estado: 'en_proceso' | 'activo' | 'vencido' | 'cancelado';
    notas?: string;
    fecha_creacion: string;
    fecha_actualizacion: string;
}

export const fetchAfiliaciones = async (): Promise<Afiliacion[]> => {
    try {
        const response = await axiosInstance.get('/afiliaciones/');
        const list = response.data?.results || response.data || [];
        return Array.isArray(list) ? list : [];
    } catch (err) {
        console.error('Error en fetchAfiliaciones:', err);
        return [];
    }
};

export const fetchAfiliacionById = async (id: number): Promise<Afiliacion> => {
    const response = await axiosInstance.get(`/afiliaciones/${id}/`);
    return response.data;
};

export const fetchAfiliacionByCliente = async (clienteId: number): Promise<Afiliacion | null> => {
    try {
        const response = await axiosInstance.get(`/afiliaciones/?cliente=${clienteId}`);
        const list = response.data?.results || response.data || [];
        return list.length > 0 ? list[0] : null;
    } catch (err) {
        console.error('Error obteniendo afiliacion de cliente:', err);
        return null;
    }
};

export const fetchMiAfiliacion = async (): Promise<Afiliacion | null> => {
    try {
        const response = await axiosInstance.get('/afiliaciones/mi_afiliacion/');
        return response.data;
    } catch (err: any) {
        if (err.response && err.response.status === 404) {
            return null;
        }
        throw err;
    }
};

export const createAfiliacion = async (data: any): Promise<Afiliacion> => {
    const isFormData = data instanceof FormData;
    const response = await axiosInstance.post('/afiliaciones/', data, {
        headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : undefined,
    });
    return response.data;
};

export const updatePasoAfiliacion = async (id: number, data: FormData): Promise<Afiliacion> => {
    const response = await axiosInstance.post(`/afiliaciones/${id}/actualizar_paso/`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};

export const ajustarHorasAfiliacion = async (
    id: number,
    horas_asignadas?: number,
    horas_consumidas?: number
): Promise<Afiliacion> => {
    const response = await axiosInstance.post(`/afiliaciones/${id}/ajustar_horas/`, {
        horas_gratuitas_asignadas: horas_asignadas,
        horas_gratuitas_consumidas: horas_consumidas,
    });
    return response.data;
};

export const fetchCorrespondenciasAfiliacion = async (id: number): Promise<any[]> => {
    try {
        const response = await axiosInstance.get(`/afiliaciones/${id}/correspondencias/`);
        const list = response.data?.results || response.data || [];
        return Array.isArray(list) ? list : [];
    } catch (err) {
        console.error('Error cargando correspondencias de afiliación:', err);
        return [];
    }
};

export const fetchPagosAfiliacion = async (id: number): Promise<any[]> => {
    try {
        const response = await axiosInstance.get(`/afiliaciones/${id}/pagos/`);
        const list = response.data?.results || response.data || [];
        return Array.isArray(list) ? list : [];
    } catch (err) {
        console.error('Error cargando pagos de afiliación:', err);
        return [];
    }
};

export const fetchConfigsDomiciliacion = async (): Promise<{
    horasRegalo: any | null;
    correspondencia: any | null;
    preciosPlan: any[];
}> => {
    try {
        const [resHoras, resCorr, resPrecios] = await Promise.allSettled([
            axiosInstance.get('/horas-regalo-config/'),
            axiosInstance.get('/configuracion-correspondencia/'),
            axiosInstance.get('/precios-planes-servicios/'),
        ]);

        const horasList = resHoras.status === 'fulfilled' ? (resHoras.value.data?.results || resHoras.value.data || []) : [];
        const corrList = resCorr.status === 'fulfilled' ? (resCorr.value.data?.results || resCorr.value.data || []) : [];
        const preciosList = resPrecios.status === 'fulfilled' ? (resPrecios.value.data?.results || resPrecios.value.data || []) : [];

        const horaDomicilio = horasList.find((h: any) => h.activo && (h.precio_plan_servicio_info?.servicio_nombre?.toLowerCase().includes('domicilio') || h.servicio_nombre?.toLowerCase().includes('domicilio'))) || horasList.find((h: any) => h.activo) || null;

        const corrDomicilio = corrList.find((c: any) => c.estado && (c.servicio_nombre?.toLowerCase().includes('domicilio') || c.descripcion?.toLowerCase().includes('correspondencia'))) || corrList.find((c: any) => c.estado) || null;

        return {
            horasRegalo: horaDomicilio,
            correspondencia: corrDomicilio,
            preciosPlan: Array.isArray(preciosList) ? preciosList : [],
        };
    } catch (e) {
        console.error('Error obteniendo configs de domiciliación:', e);
        return { horasRegalo: null, correspondencia: null, preciosPlan: [] };
    }
};

// Alias de exportación semántica
export type Domiciliacion = Afiliacion;
export const fetchDomiciliaciones = fetchAfiliaciones;
export const fetchMiDomiciliacion = fetchMiAfiliacion;
export const createDomiciliacion = createAfiliacion;
export const updatePasoDomiciliacion = updatePasoAfiliacion;
export const ajustarHorasDomiciliacion = ajustarHorasAfiliacion;
export const fetchCorrespondenciasDomiciliacion = fetchCorrespondenciasAfiliacion;
export const fetchPagosDomiciliacion = fetchPagosAfiliacion;


export const updateDatosOficinaVirtual = async (id: number, data: Partial<Afiliacion>): Promise<Afiliacion> => {
    const response = await axiosInstance.patch(`/afiliaciones/${id}/`, data);
    return response.data;
};
